import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Select } from '@/components/ui'
import MultiSelect from '@/components/ui/MultiSelect'
import { useBootstrap } from '@/hooks/useBootstrap'
import type { CenterSummary } from '@/types/api'

/**
 * Strediská školy (`sql/040`). Výbery a filtre sa ukazujú až pri viacerých
 * aktívnych strediskách (`multi`) - škola s jedným strediskom nevidí nič.
 */
export function useCenters() {
  const { data } = useBootstrap()
  const centers: CenterSummary[] = data?.centers ?? []
  return {
    centers,
    active: centers.filter((c) => c.active),
    multi: Boolean(data?.multi_center),
    nameOf: (id: number | null | undefined) => centers.find((c) => c.id === id)?.name ?? '',
    colorOf: (id: number | null | undefined) => centers.find((c) => c.id === id)?.color ?? '',
  }
}

const KEY = 'l2s:centers'
const EVENT = 'l2s:centers-changed'

function readFilter(): number[] {
  try {
    return (localStorage.getItem(KEY) ?? '')
      .split(',')
      .map(Number)
      .filter((id) => id > 0)
  } catch {
    return []
  }
}

/** Vybrané strediská - spoločné pre kalendár, platby, štatistiky a odmeny
 *  (zapamätá sa v prehliadači). Prázdny zoznam = všetky. */
export function useCenterFilter(): [number[], (ids: number[]) => void] {
  const { multi, centers } = useCenters()
  const [ids, setIds] = useState<number[]>(readFilter)
  useEffect(() => {
    const sync = () => setIds(readFilter())
    window.addEventListener(EVENT, sync)
    return () => window.removeEventListener(EVENT, sync)
  }, [])
  const set = (next: number[]) => {
    try {
      localStorage.setItem(KEY, next.join(','))
    } catch {
      /* súkromné okno - filter platí len do obnovenia stránky */
    }
    setIds(next)
    window.dispatchEvent(new Event(EVENT))
  }
  // Bez viacerých stredísk sa nefiltruje (aj keď v prehliadači ostal výber).
  const known = ids.filter((id) => centers.some((c) => c.id === id))
  return [multi ? known : [], set]
}

/** Filter stredísk (viacvýber) - len pri viacerých strediskách. */
export function CenterFilter({ className }: { className?: string }) {
  const { t } = useTranslation()
  const { active, multi } = useCenters()
  const [ids, setIds] = useCenterFilter()
  if (!multi) return null
  const selected = new Set(ids)
  return (
    <MultiSelect
      className={className}
      options={active.map((c) => ({ id: c.id, label: c.name, color: c.color || undefined }))}
      selected={selected}
      onToggle={(id) =>
        setIds(selected.has(Number(id)) ? ids.filter((x) => x !== Number(id)) : [...ids, Number(id)])
      }
      onSelectAll={(all) => setIds(all.map(Number))}
      onClearAll={() => setIds([])}
      placeholder={t('centers.all')}
      searchThreshold={0}
    />
  )
}

/** Výber jedného strediska vo formulári - len pri viacerých strediskách. */
export function CenterSelect({
  value,
  onChange,
  disabled,
}: {
  value: number | null | undefined
  onChange: (id: number) => void
  disabled?: boolean
}) {
  const { active, centers, multi } = useCenters()
  if (!multi) return null
  // Vypnuté stredisko, v ktorom záznam už je, musí ostať vo výbere.
  const options = centers.filter((c) => c.active || c.id === value)
  return (
    <Select value={value ?? active[0]?.id ?? 1} disabled={disabled} onChange={(e) => onChange(Number(e.target.value))}>
      {options.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
        </option>
      ))}
    </Select>
  )
}
